/**
 * News view (v2).
 *
 * Layout, top → bottom:
 *   1. Eight subscription multi-selects (people / project / client / industry
 *      / service / company / internal / admin). Each shows count + a quick
 *      "Subscribed to: …" line. Internal + admin are toggle-only ("Follow all").
 *   2. Today's morning briefing — large markdown with linked sources.
 *      A horizontal date slider above lets you jump to prior briefings.
 *   3. Full feed of all items from subscribed entities, sorted newest-first.
 *      Expandable — collapsed by default to keep the briefing in focus.
 *
 * Producer: the morning-briefings master agent generates today's briefing
 * at 01:00 Europe/Zurich after the three news watchers run. A "Generate now"
 * button on this page also kicks off generation on demand.
 */
import React from 'react';
import {
  listTaxonomies, listProjects, listAdminUsers,
  getMyNewsSubscriptions, putMyNewsSubscriptions,
  listMyMorningBriefings, getMyMorningBriefing, generateMyMorningBriefing,
  getMyNewsFeed,
} from '../api.js';
import { TaxonomyMultiSelect, Icon, ThumbsRow } from './primitives.jsx';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';

/* ────────────────────────────────────────────────────────────────────
 * Category metadata. Order = display order in the subscription editor.
 * ──────────────────────────────────────────────────────────────────── */
const CATEGORIES = [
  { id: 'people',    label: 'People',     itemKey: 'users',      hint: 'Colleagues whose audit-log activity & coaching nudges you want to see.' },
  { id: 'project',   label: 'Projects',   itemKey: 'projects',   hint: 'Engagements you want to watch — status flips, team changes, billings.' },
  { id: 'client',    label: 'Clients',    itemKey: 'clients',    hint: 'Customer companies — web-search news fed by the Client News Watcher.' },
  { id: 'industry',  label: 'Industries', itemKey: 'industries', hint: 'Verticals — demand-shaping events fed by the Industry News Watcher.' },
  { id: 'service',   label: 'Services',   itemKey: 'services',   hint: 'Offerings — methodology shifts fed by the Service News Watcher.' },
  { id: 'company',   label: 'Organizations',  itemKey: 'companies',  hint: 'Organizations you belong to (your employer, sister offices, partner firms).' },
  { id: 'internal',  label: 'Internal',   itemKey: null,         hint: 'Firm-internal admin actions — config changes, governance, migrations. Placeholder content for now.' },
  { id: 'admin',     label: 'Admin',      itemKey: null,         hint: 'Full firm activity feed — every state-changing event. Heavy. Subscribe only if you want it all.' },
];

const emptySubs = () => CATEGORIES.reduce((acc, c) => (acc[c.id] = [], acc), {});

/* ──────────────────────────────────────────────────────────────────── */

export const NewsView = () => {
  const [catalog, setCatalog]   = React.useState(null);
  const [subs, setSubs]         = React.useState(emptySubs());
  const [dirty, setDirty]       = React.useState(false);
  const [error, setError]       = React.useState(null);

  const [briefings, setBriefings]   = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [activeBriefing, setActiveBriefing] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);

  const [feedOpen, setFeedOpen] = React.useState(false);
  const [feedItems, setFeedItems] = React.useState(null);
  const [feedLoading, setFeedLoading] = React.useState(false);

  /* ── initial load ──────────────────────────────────────────── */
  React.useEffect(() => {
    Promise.all([
      listTaxonomies(),
      listProjects().catch(() => []),
      listAdminUsers().catch(() => []),
      getMyNewsSubscriptions(),
      listMyMorningBriefings(30),
    ])
      .then(([tax, projects, users, mine, briefs]) => {
        setCatalog({
          users: (users || []).map(u => ({ id: u._id || u.id, name: u.name || u.email, description: u.title || u.email })),
          projects: (projects || []).map(p => ({ id: p._id || p.id, name: p.name, description: p.clientName || '' })),
          clients: tax.clients || [],
          industries: tax.industries || [],
          services: tax.services || [],
          companies: tax.companies || [],
        });
        const fresh = { ...emptySubs(), ...mine };
        for (const c of CATEGORIES) if (!Array.isArray(fresh[c.id])) fresh[c.id] = [];
        setSubs(fresh);
        setBriefings(briefs || []);
        // Prefer today's row if it exists; otherwise show today as
        // pending so the user lands on the "Generate today" CTA
        // instead of yesterday's stale content.
        const todayZurich = zurichToday();
        const todayRow = (briefs || []).find(b => dateKey(b.briefingDate) === todayZurich);
        setSelectedDate(todayZurich);
        setActiveBriefing(todayRow || { briefingDate: todayZurich, pending: true, sources: [], bodyMd: '', itemCount: 0 });
      })
      .catch(err => setError(err.message));
  }, []);

  /* ── briefing date selection ───────────────────────────────── */
  const pickDate = async date => {
    setSelectedDate(date);
    const hit = briefings.find(b => dateKey(b.briefingDate) === dateKey(date));
    if (hit && hit.bodyMd) { setActiveBriefing(hit); return; }
    try {
      const fresh = await getMyMorningBriefing(date);
      if (fresh) { setActiveBriefing(fresh); return; }
      // No briefing exists for this date — flag it so the body can
      // render a clear "pending" CTA instead of stale yesterday content.
      setActiveBriefing({ briefingDate: date, pending: true, sources: [], bodyMd: '', itemCount: 0 });
    } catch (err) { setError(err.message); }
  };

  /* ── save subscriptions ──────────────────────────────────────
   * After a save we auto-regenerate today's briefing so the user
   * sees the impact of their new subscriptions immediately —
   * without a separate "Regenerate" click. (The manual button
   * lived in CompactDateStrip; it's gone now per user request.)
   */
  const save = async () => {
    setError(null);
    const next = await putMyNewsSubscriptions(subs);
    const merged = { ...emptySubs(), ...next };
    for (const c of CATEGORIES) if (!Array.isArray(merged[c.id])) merged[c.id] = [];
    setSubs(merged);
    setDirty(false);
    // Fire-and-forget regenerate so the briefing UI updates as
    // soon as Claude produces the new one. We don't await — the
    // generating flag drives a spinner inside BriefingBody.
    regenerateToday();
  };

  // Auto-save subscription changes once the user pauses. A slightly longer
  // debounce batches rapid multi-select edits into one save + one briefing
  // regeneration.
  const { status: saveStatus, error: saveError } = useAutoSave({
    dirty,
    save,
    delay: 1500,
    signature: JSON.stringify(subs),
  });

  const updateCategory = (catId, ids) => {
    setSubs(prev => ({ ...prev, [catId]: ids }));
    setDirty(true);
  };

  const toggleFollowAll = catId => {
    const isAll = subs[catId].includes('*');
    updateCategory(catId, isAll ? [] : ['*']);
  };

  /* ── manual briefing generation ────────────────────────────── */
  const regenerateToday = async () => {
    setGenerating(true); setError(null);
    try {
      const fresh = await generateMyMorningBriefing();
      setActiveBriefing(fresh);
      setSelectedDate(fresh.briefingDate);
      // Refresh the timeline so the new row appears.
      const refreshed = await listMyMorningBriefings(30);
      setBriefings(refreshed || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  /* ── full feed (lazy) ──────────────────────────────────────── */
  const openFullFeed = async () => {
    setFeedOpen(true);
    if (feedItems !== null) return;
    setFeedLoading(true);
    try {
      const data = await getMyNewsFeed({ days: 7, limit: 120 });
      setFeedItems(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setFeedLoading(false);
    }
  };

  /* ── render ────────────────────────────────────────────────── */
  if (error && !catalog) {
    return <div className="page"><ErrorBanner>{error}</ErrorBanner></div>;
  }
  if (!catalog) {
    return <div className="page"><div style={{padding:60,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>Loading…</div></div>;
  }

  return (
    <div className="page news-page">
      {error && <div style={{marginBottom:14}}><ErrorBanner>{error}</ErrorBanner></div>}

      <div className="news-layout respo-sidebar" style={{
        gap:24,
        alignItems:'start',
      }}>
        {/* Main column — briefing is the lead content */}
        <div style={{minWidth:0}}>
          <CompactDateStrip
            briefings={briefings}
            selectedDate={selectedDate}
            onPick={pickDate}
            generating={generating}
          />
          <BriefingBody briefing={activeBriefing} generating={generating}/>
          {/* Manual regenerate button removed — saving a subscription
              auto-regenerates today's briefing. The CompactDateStrip
              + BriefingBody show a "Generating…" spinner while it
              runs so the user still has feedback. */}
          <FullFeedToggle
            open={feedOpen}
            items={feedItems}
            loading={feedLoading}
            onOpen={openFullFeed}
            onClose={() => setFeedOpen(false)}
          />
        </div>

        {/* Right sidebar — page title, lede, subscriptions */}
        <aside style={{
          position:'sticky',
          top:24,
          alignSelf:'start',
          display:'flex',
          flexDirection:'column',
          gap:14,
        }}>
          <div>
            <h1 style={{margin:'0 0 6px',fontSize:22,fontFamily:'var(--font-display)',fontWeight:400}}>Morning Briefing</h1>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.45}}>
              Your personal morning briefing + everything you follow. The 01:00 Zurich master writes a fresh briefing each morning.
            </div>
          </div>
          <SubscriptionEditor
            catalog={catalog}
            subs={subs}
            onChange={updateCategory}
            onToggleAll={toggleFollowAll}
            dirty={dirty}
            saveStatus={saveStatus}
            error={saveError}
            compact
          />
        </aside>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Subscription editor
 * ──────────────────────────────────────────────────────────────────── */

const SubscriptionEditor = ({ catalog, subs, onChange, onToggleAll, dirty, saveStatus, error, compact = false }) => {
  // Default to collapsed — most users glance at the morning briefing,
  // they only edit subscriptions occasionally. Expanding is a single
  // click; the summary row makes the current state legible without it.
  const [expanded, setExpanded] = React.useState(false);

  // Build the flat summary row of every active subscription, tagged
  // with its category so the user can scan "do I follow what I think
  // I follow" without expanding the editor.
  const summaryChips = [];
  for (const cat of CATEGORIES) {
    const ids = subs[cat.id] || [];
    if (ids.length === 0) continue;
    if (!cat.itemKey) {
      // internal / admin → boolean
      if (ids.includes('*')) summaryChips.push({ catId: cat.id, catLabel: cat.label, name: 'all' });
      continue;
    }
    const items = catalog[cat.itemKey] || [];
    const byId = new Map(items.map(i => [i.id, i]));
    for (const id of ids) {
      const item = byId.get(id);
      summaryChips.push({ catId: cat.id, catLabel: cat.label, name: item?.name || id });
    }
  }
  const totalCount = summaryChips.length;

  // Compact mode shrinks paddings, font sizes, and stacks category
  // cards as a single column to fit the right-sidebar (320px wide).
  const padCollapsed = compact ? '10px 12px' : '14px 18px';
  const padExpanded  = compact ? '14px 16px 18px' : '18px 22px 22px';
  const gridCols     = compact ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))';
  const titleSize    = compact ? (expanded ? 15 : 13.5) : (expanded ? 18 : 15);

  return (
    <section style={{
      background:'var(--bg-alt)', borderRadius: compact ? 12 : 16,
      padding: expanded ? padExpanded : padCollapsed,
      transition:'padding 0.15s',
    }}>
      <div
        style={{
          display:'flex',alignItems:'center',justifyContent:'space-between',
          gap:8,flexWrap:'wrap',
          cursor: expanded ? 'default' : 'pointer',
          marginBottom: expanded ? 12 : 0,
        }}
        onClick={() => { if (!expanded) setExpanded(true); }}
      >
        <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0,flex:1}}>
          <button
            type="button"
            aria-label={expanded ? 'Collapse subscriptions' : 'Expand subscriptions'}
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            style={{
              background:'transparent',border:0,cursor:'pointer',padding:2,
              display:'inline-flex',alignItems:'center',
              transition:'transform 0.15s',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              color:'var(--fg-muted)',
            }}
          >
            <Icon name="chevron-right" size={12}/>
          </button>
          <div style={{minWidth:0,flex:1}}>
            <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
              <div style={{fontFamily:'var(--font-display)',fontSize:titleSize,lineHeight:1.2}}>Your subscriptions</div>
              <span style={{fontSize:10.5,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>
                {totalCount} active
              </span>
            </div>
            {expanded && !compact && (
              <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:4}}>
                What you pick here shapes your morning briefing and the full feed below.
              </div>
            )}
            {!expanded && summaryChips.length > 0 && (
              <div style={{
                marginTop:6,display:'flex',flexWrap:'wrap',gap:4,
                fontSize:10.5,lineHeight:1.4,
              }}>
                {summaryChips.slice(0, compact ? 12 : 24).map((c, i) => (
                  <span key={`${c.catId}-${i}`} style={{
                    background:'var(--bg)',padding:'1px 7px',borderRadius:8,
                    border:'1px solid var(--border)',
                    whiteSpace:'nowrap',
                  }}>
                    <span style={{color:'var(--fg-muted)'}}>{c.catLabel}:</span>{' '}
                    <span>{c.name}</span>
                  </span>
                ))}
                {summaryChips.length > (compact ? 12 : 24) && (
                  <span style={{fontSize:10.5,color:'var(--fg-muted)',alignSelf:'center'}}>
                    +{summaryChips.length - (compact ? 12 : 24)} more
                  </span>
                )}
              </div>
            )}
            {!expanded && summaryChips.length === 0 && (
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:6,fontStyle:'italic'}}>
                Nothing subscribed yet — click to pick.
              </div>
            )}
          </div>
        </div>
        {expanded && (
          <div style={{display:'flex',alignItems:'center',gap:8}} onClick={e => e.stopPropagation()}>
            <SaveStatus status={saveStatus} error={error} dirty={dirty}/>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{
          display:'grid',
          gridTemplateColumns:gridCols,
          gap: compact ? 10 : 18,
        }}>
          {CATEGORIES.map(cat => (
            <CategoryPicker
              key={cat.id}
              cat={cat}
              items={cat.itemKey ? (catalog[cat.itemKey] || []) : []}
              selectedIds={subs[cat.id] || []}
              onChange={ids => onChange(cat.id, ids)}
              onToggleAll={() => onToggleAll(cat.id)}
              compact={compact}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const CategoryPicker = ({ cat, items, selectedIds, onChange, onToggleAll, compact = false }) => {
  const isBoolean = !cat.itemKey;   // internal + admin → toggle only
  const isFollowAll = (selectedIds || []).includes('*');
  const pad = compact ? '10px 12px' : 14;
  const titleSize = compact ? 12 : 13;

  if (isBoolean) {
    return (
      <div style={{padding:pad,background:'var(--bg)',borderRadius:10,border:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,gap:8}}>
          <div style={{fontSize:titleSize,fontWeight:600}}>{cat.label}</div>
          <span className={`tag ${isFollowAll ? 'tag--orange' : ''}`} style={{cursor:'pointer',fontSize:10.5}} onClick={onToggleAll}>
            {isFollowAll ? 'Following all' : 'Not following'}
          </span>
        </div>
        {!compact && <div style={{fontSize:11.5,color:'var(--fg-muted)',lineHeight:1.4}}>{cat.hint}</div>}
      </div>
    );
  }

  const selectedItems = items.filter(i => selectedIds.includes(i.id));
  const summary = selectedIds.length === 0
    ? 'no subscriptions yet'
    : `Subscribed: ${selectedItems.map(i => i.name).join(', ') || `${selectedIds.length} item${selectedIds.length === 1 ? '' : 's'}`}`;

  return (
    <div style={{padding:pad,background:'var(--bg)',borderRadius:10,border:'1px solid var(--border)'}}>
      <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',marginBottom:6,gap:8}}>
        <div style={{fontSize:titleSize,fontWeight:600}}>{cat.label}</div>
        <span style={{fontSize:10.5,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>
          {selectedIds.length} selected
        </span>
      </div>
      <TaxonomyMultiSelect
        items={items}
        selectedIds={selectedIds}
        onChange={onChange}
        hint={compact ? null : cat.hint}
        emptyMessage={`No ${cat.label.toLowerCase()} in the firm catalog yet.`}
      />
      <div style={{
        marginTop:6,fontSize:compact ? 10.5 : 11.5,color:'var(--fg-muted)',
        lineHeight:1.4,
        overflow:'hidden',textOverflow:'ellipsis',display:'-webkit-box',
        WebkitLineClamp:2,WebkitBoxOrient:'vertical',
      }}>
        {summary}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Briefing timeline (date slider)
 * ──────────────────────────────────────────────────────────────────── */

// Today in Europe/Zurich, as 'YYYY-MM-DD' — matches the format the
// server writes into morning_briefings.briefing_date. Using UTC here
// drifts off by a day in the late evening (Zurich time) and breaks the
// "Today" indicator on the chip.
// Server has historically returned briefingDate as either a clean
// "YYYY-MM-DD" or a full ISO timestamp ("2026-05-17T00:00:00Z").
// Slicing to the first 10 chars before comparing normalises both
// shapes so equality checks against zurichToday() / zurichYesterday()
// don't silently fail and mis-label today's row (the visible
// "Today is still not correctly shown" symptom).
const dateKey = (d) => String(d || '').slice(0, 10);

function zurichToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

// Same shape as zurichToday() but for "yesterday" — used to label
// the chip "Yesterday" instead of repeating the date. Subtracting 24h
// from the current moment then formatting in Zurich gets DST/timezone
// transitions right without bespoke arithmetic.
function zurichYesterday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date(Date.now() - 24 * 60 * 60 * 1000));
}

/* ────────────────────────────────────────────────────────────────────
 * Compact horizontal date strip — slim chips, not the old big pills.
 * Sits directly above the briefing body. Today's chip carries an
 * orange dot prefix when the chip itself isn't active.
 * ──────────────────────────────────────────────────────────────────── */

const CompactDateStrip = ({ briefings, selectedDate, onPick, generating }) => {
  const today = zurichToday();
  const yesterday = zurichYesterday();
  const hasToday = briefings.some(b => dateKey(b.briefingDate) === today);
  const rows = hasToday
    ? briefings
    : [{ briefingDate: today, itemCount: 0, pending: true }, ...briefings];

  const stripRef = React.useRef(null);
  const activeRef = React.useRef(null);
  React.useEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    try { el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' }); }
    catch { el.scrollIntoView({ block: 'nearest', inline: 'center' }); }
  }, [selectedDate]);

  return (
    <div className="news-strip">
      <div ref={stripRef} className="news-strip__scroll">
        {rows.map(b => {
          const key = dateKey(b.briefingDate);
          const active = dateKey(selectedDate) === key;
          return (
            <DateChip
              key={key}
              briefing={b}
              chipRef={active ? activeRef : null}
              active={active}
              isToday={key === today}
              isYesterday={key === yesterday}
              onClick={() => onPick(key)}
            />
          );
        })}
      </div>
      {/* The right-hand Regenerate button used to live here — replaced
          by auto-regenerate on subscription save. A tiny "Generating…"
          hint takes its place while the briefing is being rebuilt so
          the user knows something is happening. */}
      {generating && (
        <span style={{fontSize:11.5,color:'var(--fg-muted)',fontStyle:'italic',marginLeft:'auto',padding:'0 8px'}}>
          Generating…
        </span>
      )}
    </div>
  );
};

const DateChip = ({ briefing, active, isToday, isYesterday, onClick, chipRef }) => {
  // Defensive parse: briefingDate is the server-side
  // morning_briefings.briefing_date column, which the API has
  // historically returned as either 'YYYY-MM-DD' (intended) or a
  // full ISO timestamp (one of the cron paths re-serialises it).
  // Appending 'T00:00:00Z' to an already-ISO string produces an
  // Invalid Date, which is what swallowed today's chip rendering.
  // Slice to the first 10 chars before constructing the Date so
  // either input shape resolves cleanly.
  const dateOnly = String(briefing.briefingDate || '').slice(0, 10);
  const d = new Date(dateOnly + 'T00:00:00Z');
  const day = d.toLocaleDateString(undefined, { weekday: 'short' });
  const dayN = d.getUTCDate();
  const mon = d.toLocaleDateString(undefined, { month: 'short' });
  const pending = briefing.pending === true;
  const empty = !pending && briefing.itemCount === 0;
  const title = pending
    ? 'No briefing for today yet — click Generate today'
    : empty
      ? `${briefing.briefingDate}: briefing exists but produced no items`
      : `${briefing.briefingDate}: ${briefing.itemCount} item${briefing.itemCount === 1 ? '' : 's'}`;

  const bg = active ? 'var(--orange)' : (isToday ? 'var(--bg)' : 'var(--bg-alt)');
  const color = active ? 'white' : 'inherit';
  const borderStyle = isToday && !active ? 'dashed' : 'solid';
  const borderColor = isToday && !active ? 'var(--orange)' : 'var(--border)';

  return (
    <div
      ref={chipRef || undefined}
      onClick={onClick}
      title={title}
      style={{
        cursor:'pointer',flexShrink:0,
        padding:'4px 10px',
        borderRadius:99,
        border:`1px ${borderStyle} ${borderColor}`,
        background: bg,
        color,
        fontSize:11.5,
        lineHeight:1.4,
        whiteSpace:'nowrap',
        display:'inline-flex',alignItems:'center',gap:6,
        opacity: pending ? 0.85 : 1,
        fontWeight: active ? 600 : 500,
      }}
    >
      {isToday && (
        <span style={{
          width:6,height:6,borderRadius:'50%',flexShrink:0,
          background: active ? 'white' : 'var(--orange)',
        }} aria-label="today"/>
      )}
      {isToday ? (
        <span style={{fontWeight:600,letterSpacing:'0.01em'}}>Today</span>
      ) : isYesterday ? (
        <span style={{fontWeight:600,letterSpacing:'0.01em'}}>Yesterday</span>
      ) : (
        <>
          <span style={{textTransform:'uppercase',letterSpacing:'0.03em',fontSize:10,opacity:0.75}}>{day}</span>
          <span style={{fontFamily:'JetBrains Mono',fontWeight:600}}>{dayN}</span>
          <span style={{fontSize:10,opacity:0.7}}>{mon}</span>
        </>
      )}
      {pending && <span style={{fontSize:10,fontStyle:'italic',opacity:0.7}}>· pending</span>}
      {empty && !pending && <span style={{fontSize:10,fontStyle:'italic',opacity:0.55}}>· empty</span>}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Briefing body (markdown render + sources)
 * ──────────────────────────────────────────────────────────────────── */

const BriefingBody = ({ briefing, generating }) => {
  if (!briefing) {
    return (
      <section style={{padding:24,background:'var(--bg-alt)',borderRadius:14,marginBottom:24,fontSize:13,color:'var(--fg-muted)',textAlign:'center'}}>
        Select a date above to read that briefing.
      </section>
    );
  }
  if (briefing.pending) {
    return (
      <section style={{
        padding:'28px 28px',background:'var(--bg-alt)',borderRadius:16,
        marginBottom:24,textAlign:'center',
      }}>
        <div style={{fontFamily:'var(--font-display)',fontSize:20,marginBottom:6}}>
          No briefing for {briefing.briefingDate} yet
        </div>
        <div style={{fontSize:13,color:'var(--fg-muted)',marginBottom:16,lineHeight:1.5}}>
          The 01:00 Zurich master agent writes one each morning across your subscriptions. Save a subscription change above to trigger a fresh briefing now.
        </div>
        {generating && (
          <div style={{fontSize:13,color:'var(--orange)',fontStyle:'italic'}}>Generating…</div>
        )}
      </section>
    );
  }
  const sources = briefing.sources || [];
  return (
    <section className="briefing-card" style={{
      padding:'24px 28px 28px',
      background:'var(--bg-alt)',
      borderRadius:16,
      marginBottom:24,
      lineHeight:1.55,
    }}>
      <div style={{
        fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',
        color:'var(--fg-muted)',marginBottom:6,fontWeight:600,
      }}>
        {briefing.briefingDate} · {briefing.itemCount} item{briefing.itemCount === 1 ? '' : 's'}
      </div>
      <div className="md-body" style={{fontSize:14}}>
        <MarkdownView source={briefing.bodyMd || ''}/>
      </div>

      {sources.length > 0 && (
        <div style={{marginTop:24,paddingTop:18,borderTop:'1px solid var(--border)'}}>
          <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:10,fontWeight:600}}>
            Sources ({sources.length})
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {sources.map(s => <SourceRow key={s.newsId} source={s}/>)}
          </div>
        </div>
      )}

      <div style={{marginTop:20,paddingTop:14,borderTop:'1px solid var(--border)'}}>
        <ThumbsRow
          target={{ type: 'morning_briefing', id: briefing.id, label: briefing.briefingDate }}
          snapshots={{
            intent: `Read the personal morning briefing for ${briefing.briefingDate}`,
            aiDid: `Drafted a letter-style briefing summarising ${briefing.itemCount} item${briefing.itemCount === 1 ? '' : 's'} across subscribed entities.`,
            link: `#news`,
          }}
        />
      </div>
    </section>
  );
};

const SourceRow = ({ source }) => {
  return (
    <div style={{
      display:'flex',alignItems:'center',gap:10,
      padding:'8px 10px',background:'var(--bg)',borderRadius:8,
      fontSize:12.5,
    }}>
      <span className="tag" style={{fontSize:10,padding:'2px 6px',background:'color-mix(in srgb, var(--orange) 14%, transparent)',color:'var(--orange)'}}>
        {source.category}
      </span>
      <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
        {source.entityLabel && <span style={{fontStyle:'italic',color:'var(--fg-muted)',marginRight:6}}>{source.entityLabel} ·</span>}
        {source.title}
      </span>
      {source.source && <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{source.source}</span>}
      {source.url && (
        <a href={source.url} target="_blank" rel="noreferrer noopener"
           className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
          Open <Icon name="external-link" size={10}/>
        </a>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Full feed (lazy)
 * ──────────────────────────────────────────────────────────────────── */

const FullFeedToggle = ({ open, items, loading, onOpen, onClose }) => {
  if (!open) {
    return (
      <section style={{textAlign:'center',marginBottom:32}}>
        <button className="btn-secondary" style={{padding:'8px 18px',fontSize:13}} onClick={onOpen}>
          Open full feed of subscribed items
        </button>
      </section>
    );
  }
  return (
    <section style={{marginBottom:40}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
        <div style={{fontFamily:'var(--font-display)',fontSize:18}}>
          Full feed
          {items && <span style={{fontSize:12,color:'var(--fg-muted)',marginLeft:10,fontFamily:'inherit'}}>{items.length} item{items.length === 1 ? '' : 's'}</span>}
        </div>
        <button className="btn-secondary" style={{padding:'4px 12px',fontSize:12}} onClick={onClose}>Hide</button>
      </div>

      {loading && <div style={{padding:24,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>Loading feed…</div>}
      {!loading && items && items.length === 0 && (
        <div style={{padding:20,background:'var(--bg-alt)',borderRadius:12,fontSize:13,color:'var(--fg-muted)',textAlign:'center'}}>
          No items in the last 7 days across your subscriptions. Adjust them above to see more.
        </div>
      )}
      {!loading && items && items.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {items.map(item => <FeedItemRow key={item.id} item={item}/>)}
        </div>
      )}
    </section>
  );
};

const FeedItemRow = ({ item }) => {
  return (
    <div style={{
      padding:'12px 14px',
      background:'var(--bg-alt)',borderRadius:10,
      display:'grid',gridTemplateColumns:'90px 1fr auto',gap:14,alignItems:'start',
    }}>
      <div style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono',whiteSpace:'nowrap'}}>
        {relTime(item.publishedAt)}
      </div>
      <div style={{minWidth:0}}>
        <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:3,flexWrap:'wrap'}}>
          <span className="tag" style={{fontSize:10,padding:'1px 7px',background:'color-mix(in srgb, var(--orange) 14%, transparent)',color:'var(--orange)'}}>
            {item.category}
          </span>
          {item.entityLabel && <span style={{fontSize:11.5,color:'var(--fg-muted)',fontStyle:'italic'}}>{item.entityLabel}</span>}
        </div>
        <div style={{fontSize:13.5,fontWeight:500,lineHeight:1.35}}>{item.title}</div>
        {item.bodyMd && <div style={{fontSize:12.5,color:'var(--fg-muted)',marginTop:4,lineHeight:1.45}}>{item.bodyMd.slice(0, 240)}</div>}
        {item.whyRelevant && <div style={{fontSize:12,color:'var(--orange)',marginTop:4,fontStyle:'italic'}}>Why: {item.whyRelevant}</div>}
        <div style={{marginTop:6}}>
          <ThumbsRow
            target={{ type: 'news_item', id: item.id, label: item.title.slice(0, 120) }}
            snapshots={{
              intent: `Surface ${item.category} news ${item.entityLabel ? `about ${item.entityLabel}` : ''}`.trim(),
              aiDid: item.bodyMd || item.title,
              link: item.url || '#news',
            }}
            compact
          />
        </div>
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        {item.source && <span style={{fontSize:11,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>{item.source}</span>}
        {item.url && (
          <a href={item.url} target="_blank" rel="noreferrer noopener"
             className="btn-secondary" style={{padding:'3px 10px',fontSize:11,textDecoration:'none'}}>
            Open
          </a>
        )}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * Misc helpers
 * ──────────────────────────────────────────────────────────────────── */

const ErrorBanner = ({ children }) => (
  <div style={{padding:12,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{children}</div>
);

function relTime(at) {
  const d = at instanceof Date ? at : new Date(at);
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60)         return 'just now';
  if (sec < 3600)       return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)      return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7)  return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
